// src/features/master-data/pages/MasterDataListPage.js
import React, { useState } from 'react';
import { Plus, Download, Upload, RefreshCw } from 'lucide-react';
import { useMasterData } from '../hooks/useMasterData';
import MasterDataTable from '../components/MasterDataTable';
import MasterDataForm from '../components/MasterDataForm';
import MasterDataFilters from '../components/MasterDataFilters';
import PageHeader from '../../../shared/components/PageHeader';
import Button from '../../../shared/components/Button';
import Card from '../../../shared/components/Card';
import Pagination from '../../../shared/components/Pagination';
import ConfirmDialog from '../../../shared/components/ConfirmDialog';
import { displayConfig, formFields } from '../config/masterDataConfig';
import { masterDataApi } from '../services/masterDataApi';

const MasterDataListPage = ({ type }) => {
  const config = displayConfig[type];
  const fields = formFields[type] || [];
  
  const [showForm, setShowForm] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [formOptions, setFormOptions] = useState({});
  const [viewItem, setViewItem] = useState(null);

  const {
    data,
    loading,
    pagination,
    filters,
    sortColumn,
    sortDirection,
    create,
    update,
    delete: deleteItem,
    bulkDelete,
    toggleStatus,
    import: importData,
    export: exportData,
    updateFilters,
    resetFilters,
    changePage,
    changePageSize,
    handleSort,
  } = useMasterData(type);
  //console.log(data);

  const handleAdd = () => {
    setSelectedItem(null);
    loadFormOptions();
    setShowForm(true);
  };

  const handleEdit = (item) => {
    setSelectedItem(item);
    loadFormOptions();
    setShowForm(true);
  };

  const handleView = (item) => {
    // Navigate to detail page or show detail modal
    setViewItem(item);
    console.log('View item:', item);
  };

  const handleDelete = (item) => {
    setItemToDelete(item);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (itemToDelete) {
      await deleteItem(itemToDelete.id);
      setShowDeleteConfirm(false);
      setItemToDelete(null);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedRows.length > 0) {
      await bulkDelete(selectedRows);
      setSelectedRows([]);
    }
  };

  const handleToggleStatus = async (item) => {
    await toggleStatus(item.id, item.is_active);
  };

  const handleDuplicate = async (item) => {
    const { id, created_at, updated_at, ...itemData } = item;
    itemData.name = `${itemData.name} (Copy)`;
    await create(itemData);
  };

  const handleImport = async (event) => {
    const file = event.target.files[0];
    if (file) {
      await importData(file);
    }
  };

  const loadFormOptions = async () => {
    // Load options for select fields
    const options = {};
    for (const field of fields) {
      if (field.type === 'select' && field.options === 'dynamic') {
        try {
          const response = await masterDataApi.getOptions(type, field.name);
          options[field.name] = response.data;
        } catch (error) {
          console.error(`Failed to load options for ${field.name}:`, error);
        }
      }
    }
    setFormOptions(options);
  };

  const handleSelectAll = () => {
    if (selectedRows.length === data.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows(data.map(item => item.id));
    }
  };

  const handleSelectRow = (id) => {
    setSelectedRows(prev =>
      prev.includes(id) ? prev.filter(rowId => rowId !== id) : [...prev, id]
    );
  };

  const Icon = config?.icon;
{viewItem && (
  <Card>
    <h3 className="text-lg font-semibold mb-4">Item Details</h3>
    <pre>{JSON.stringify(viewItem, null, 2)}</pre>

    <Button onClick={() => setViewItem(null)}>
      Close
    </Button>
  </Card>
)}
  return (
    <div className="space-y-6">
      {/* Page Header */}
      <PageHeader
        title={config?.title || type}
        description={config?.description || `Manage ${type}`}
        icon={Icon}
        color={config?.color}
        actions={
          <div className="flex space-x-3">
            <input
              type="file"
              id="import-file"
              accept=".csv,.xlsx,.xls"
              className="hidden"
              onChange={handleImport}
            />
            <Button
              variant="secondary"
              icon={Upload}
              onClick={() => document.getElementById('import-file').click()}
            >
              Import
            </Button>
            <Button
              variant="secondary"
              icon={Download}
              onClick={() => exportData()}
            >
              Export
            </Button>
            <Button
              variant="secondary"
              icon={RefreshCw}
              onClick={() => window.location.reload()}
            >
              Refresh
            </Button>
            <Button
              variant="primary"
              icon={Plus}
              onClick={handleAdd}
            >
              Add New
            </Button>
          </div>
        }
      />

      {/* Filters */}
      <MasterDataFilters
        filters={filters}
        onFilterChange={updateFilters}
        onReset={resetFilters}
        fields={fields}
      />

      {/* Bulk Actions */}
      {selectedRows.length > 0 && (
        <Card className="bg-yellow-50 border-yellow-200">
          <div className="flex items-center justify-between">
            <p className="text-sm text-yellow-700">
              {selectedRows.length} item(s) selected
            </p>
            <div className="flex space-x-3">
              <Button
                variant="danger"
                size="sm"
                onClick={handleBulkDelete}
              >
                Delete Selected
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setSelectedRows([])}
              >
                Clear Selection
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Table */}
      <MasterDataTable
        type={type}
        data={data}
        loading={loading}
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        onSort={handleSort}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onView={handleView}
        onToggleStatus={handleToggleStatus}
        onDuplicate={handleDuplicate}
        selectedRows={selectedRows}
        onSelectRow={handleSelectRow}
        onSelectAll={handleSelectAll}
      />

      {/* Pagination */}
      <Pagination
        currentPage={pagination.currentPage}
        totalPages={pagination.totalPages}
        totalItems={pagination.totalItems}
        pageSize={pagination.perPage}
        onPageChange={changePage}
        onPageSizeChange={changePageSize}
      />

      {/* Form Modal */}
      <MasterDataForm
        type={type}
        isOpen={showForm}
        onClose={() => {
          setShowForm(false);
          setSelectedItem(null);
        }}
        onSubmit={selectedItem ? update.bind(null, selectedItem.id) : create}
        initialData={selectedItem}
        options={formOptions}
      />

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setItemToDelete(null);
        }}
        onConfirm={confirmDelete}
        title="Delete Item"
        message={`Are you sure you want to delete "${itemToDelete?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  );
};

export default MasterDataListPage;